import { disableConvexAuth, enableConvexAuth } from './storage/featureFlags';

export const TEST_VIEWER_CTX_KEY = 'bmg_test_viewer_context';

const isDebugGlobalsEnabled = (nodeEnv = process.env.NODE_ENV) => nodeEnv !== 'production';

const deleteKeys = (windowRef, keys) => {
  if (!windowRef) return;
  keys.forEach(key => {
    try { delete windowRef[key]; } catch (_) { /* noop */ }
  });
};

export const shouldShowAuthRollbackHint = (nodeEnv = process.env.NODE_ENV) => isDebugGlobalsEnabled(nodeEnv);

export const installConvexAuthFlagGlobals = (
  windowRef = typeof window !== 'undefined' ? window : null,
  { nodeEnv = process.env.NODE_ENV } = {}
) => {
  const keys = ['__enableConvexAuth__', '__disableConvexAuth__'];
  if (!windowRef || !isDebugGlobalsEnabled(nodeEnv)) {
    deleteKeys(windowRef, keys);
    return () => {};
  }

  windowRef.__enableConvexAuth__ = enableConvexAuth;
  windowRef.__disableConvexAuth__ = disableConvexAuth;

  return () => deleteKeys(windowRef, keys);
};

export const installTestViewerContextGlobals = (
  windowRef = typeof window !== 'undefined' ? window : null,
  { nodeEnv = process.env.NODE_ENV } = {}
) => {
  const keys = [
    '__devSetTestViewerContext__',
    '__devClearTestViewerContext__',
    '__setTestViewerContext__',
    '__clearTestViewerContext__',
  ];
  if (!windowRef || !isDebugGlobalsEnabled(nodeEnv)) {
    deleteKeys(windowRef, keys);
    return () => {};
  }

  const setTestViewerContext = (ctx) => {
    if (!ctx || (ctx.viewerKind !== 'dm' && ctx.viewerKind !== 'player')) {
      console.error('__devSetTestViewerContext__: expected {viewerKind: "dm"|"player", viewerCharId?}');
      return;
    }
    if (ctx.viewerKind === 'player' && !ctx.viewerCharId) {
      console.error('__devSetTestViewerContext__: player kind requires viewerCharId');
      return;
    }
    windowRef.sessionStorage.setItem(TEST_VIEWER_CTX_KEY, JSON.stringify(ctx));
    console.log('Test viewer context set. Reloading so driver re-subscribes...', ctx);
    windowRef.location.reload();
  };

  const clearTestViewerContext = () => {
    windowRef.sessionStorage.removeItem(TEST_VIEWER_CTX_KEY);
    console.log('Test viewer context cleared. Reloading...');
    windowRef.location.reload();
  };

  windowRef.__devSetTestViewerContext__ = setTestViewerContext;
  windowRef.__devClearTestViewerContext__ = clearTestViewerContext;
  deleteKeys(windowRef, ['__setTestViewerContext__', '__clearTestViewerContext__']);

  return () => deleteKeys(windowRef, keys);
};

export const publishViewerContextDebugGlobal = (
  viewerContext,
  windowRef = typeof window !== 'undefined' ? window : null,
  { nodeEnv = process.env.NODE_ENV } = {}
) => {
  const keys = ['__devViewerContext__', '__viewerContext__'];
  if (!windowRef || !isDebugGlobalsEnabled(nodeEnv)) {
    deleteKeys(windowRef, keys);
    return () => {};
  }

  deleteKeys(windowRef, ['__viewerContext__']);
  windowRef.__devViewerContext__ = viewerContext;
  return () => deleteKeys(windowRef, ['__devViewerContext__']);
};

export const publishConvexAuthDebugGlobals = (
  { signOut, isLoading, isAuthenticated },
  windowRef = typeof window !== 'undefined' ? window : null,
  { nodeEnv = process.env.NODE_ENV } = {}
) => {
  const keys = [
    '__devConvexAuthSignOut__',
    '__devConvexAuthState__',
    '__convexAuthSignOut__',
    '__convexAuthState__',
  ];
  if (!windowRef || !isDebugGlobalsEnabled(nodeEnv)) {
    deleteKeys(windowRef, keys);
    return () => {};
  }

  deleteKeys(windowRef, ['__convexAuthSignOut__', '__convexAuthState__']);
  windowRef.__devConvexAuthSignOut__ = () => signOut();
  windowRef.__devConvexAuthState__ = { isLoading, isAuthenticated };
  return () => deleteKeys(windowRef, ['__devConvexAuthSignOut__', '__devConvexAuthState__']);
};
